import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InfoCircleOutlined, CheckCircleOutlined, WarningOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { ProjectConfig, DeploymentType } from '../../../types/deployApp.type';
import { KubiyComputeSizesTable } from '../../../components/shared';
import { useSettings } from '../../../contexts/SettingsContext';

export type ProjectTypeItem = {
  id: string;
  name: string;
  icon: React.ReactNode;
  badge?: string;
  description?: string;
};

export type AvailablePlansData = {
  type: string;
  deploymentType: DeploymentType;
  plans: Array<{
    id: string;
    name: string;
    price: number | null;
    features: string[];
    badge?: string;
    title?: string;
    subtitle?: string;
    description?: string;
    badgeSubtitle?: string;
    traffic?: string;
    trafficMessage?: string;
  }>;
} | null;

export interface Step1DeployAppProps {
  config: ProjectConfig;
  setConfig: React.Dispatch<React.SetStateAction<ProjectConfig>>;
  selectedPlan: string | null;
  setSelectedPlan: React.Dispatch<React.SetStateAction<string | null>>;
  networkMode: 'shared' | 'tenant' | null;
  setNetworkMode: React.Dispatch<React.SetStateAction<'shared' | 'tenant' | null>>;
  computeMode: 'shared' | 'dedicated' | null;
  setComputeMode: React.Dispatch<React.SetStateAction<'shared' | 'dedicated' | null>>;
  availablePlans: AvailablePlansData;
  getDeploymentBadge: (type: DeploymentType) => { emoji: string; text: string; color: string } | null;
  getPlanType: (planId: string) => 'shared' | 'dedicated' | null;
  staticProjectTypes: ProjectTypeItem[];
  backendProjectTypes: ProjectTypeItem[];
  projectTypeDescriptions: Record<string, string>;
  projectTypes: ProjectTypeItem[];
  isStaticTechnology: (type: string) => boolean;
  isSSRTechnology: (type: string, framework: string) => boolean;
  isSSGTechnology: (type: string, framework: string) => boolean;
  onVerifySubdomain?: (name: string, appType: 'static' | 'server') => Promise<{ available: boolean }>;
  hideInfrastructureSection?: boolean;
  hideAvailablePlansSection?: boolean;
}

const Step1DeployApp: React.FC<Step1DeployAppProps> = ({
  config,
  setConfig,
  selectedPlan,
  setSelectedPlan,
  networkMode,
  setNetworkMode,
  computeMode,
  setComputeMode,
  availablePlans,
  getDeploymentBadge,
  getPlanType,
  staticProjectTypes,
  backendProjectTypes,
  projectTypeDescriptions,
  projectTypes,
  isStaticTechnology,
  isSSRTechnology,
  isSSGTechnology,
  onVerifySubdomain,
  hideInfrastructureSection = false,
  hideAvailablePlansSection = false,
}) => {
  const { t } = useTranslation();
  const {
    isAppDeployStaticEnabled,
    isAppDeployServerEnabled,
    isStaticFrameworkVisible,
    isServerRuntimeVisible,
  } = useSettings();
  const [subdomainCheck, setSubdomainCheck] = useState<'idle' | 'checking' | 'available' | 'unavailable'>('idle');

  // Tarjetas de tecnología que el admin dejó visibles. Si el admin apagó el
  // modo (static/server) por completo, la lista queda vacía y su sección se
  // oculta. `missing = visible` por defecto.
  const visibleStaticTypes = isAppDeployStaticEnabled()
    ? staticProjectTypes.filter((tp) => isStaticFrameworkVisible(tp.id))
    : [];
  const visibleBackendTypes = isAppDeployServerEnabled()
    ? backendProjectTypes.filter((tp) => isServerRuntimeVisible(tp.id))
    : [];

  const handleSubdomainChange = (value: string) => {
    setConfig({ ...config, subdomain: value.replace(/\s/g, '').toLowerCase() });
    setSubdomainCheck('idle');
  };

  const handleVerifySubdomain = async () => {
    const sub = (config.subdomain || '').trim();
    if (!sub || !onVerifySubdomain) return;
    const appType: 'static' | 'server' = (isStaticTechnology(config.type) || isSSGTechnology(config.type, config.framework)) ? 'static' : 'server';
    setSubdomainCheck('checking');
    try {
      const result = await onVerifySubdomain(sub, appType);
      setSubdomainCheck(result.available ? 'available' : 'unavailable');
    } catch {
      setSubdomainCheck('unavailable');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('deployApp.step1.appName')} *
        </label>
        <input
          type="text"
          value={config.name}
          onChange={(e) => setConfig({ ...config, name: e.target.value })}
          placeholder="my-awesome-app"
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('deployApp.step1.description')}
        </label>
        <textarea
          value={config.description}
          onChange={(e) => setConfig({ ...config, description: e.target.value })}
          placeholder="Brief description of your app..."
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
          {t('deployApp.step1.appType')} *
        </label>

        {/* Tecnologías estáticas */}
        {visibleStaticTypes.length > 0 && (
        <div className="mb-8">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            {t('deployApp.staticFrontendApps.title')}
          </h3>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-500 p-4 mb-4 rounded-r-lg">
            <div className="flex items-start gap-2">
              <InfoCircleOutlined className="text-yellow-600 dark:text-yellow-400 text-lg mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-yellow-900 dark:text-yellow-300 mb-1">
                  {t('deployApp.staticFrontendApps.affordablePlansTitle')}
                </p>
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  {t('deployApp.staticFrontendApps.affordablePlansDesc')}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {visibleStaticTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => setConfig({ ...config, type: type.id, framework: '', runtime: undefined })}
                className={`p-4 border-2 rounded-lg text-left transition-all ${
                  config.type === type.id
                    ? 'border-blue-600 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
                }`}
              >
                {/* Badge para HTML + JS AI */}
                {type.badge && (
                  <span className="absolute -top-2 -right-2 text-xs px-2 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full font-bold shadow-lg animate-pulse">
                    {type.badge}
                  </span>
                )}

                <div className="mb-2 text-gray-700 dark:text-gray-300 flex items-center justify-start">
                  {type.icon}
                </div>
                <div className="font-medium text-gray-900 dark:text-white">{type.name}</div>

                {/* Descripción adicional para HTML + JS */}
                {type.description && (
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    {type.description}
                  </p>
                )}
              </button>
            ))}
          </div>
        </div>
        )}

        {/* Tecnologías con backend */}
        {visibleBackendTypes.length > 0 && (
        <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            {t('deployApp.backendApps.title')}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {visibleBackendTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => setConfig({ ...config, type: type.id, framework: '', runtime: undefined })}
                className={`p-4 border-2 rounded-lg text-left transition-all ${
                  config.type === type.id
                    ? 'border-blue-600 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
                }`}
              >
                <div className="mb-2 text-gray-700 dark:text-gray-300 flex items-center justify-start">
                  {type.icon}
                </div>
                <div className="font-medium text-gray-900 dark:text-white">{type.name}</div>
              </button>
            ))}
          </div>
        </div>
        )}

        {config.type && projectTypeDescriptions[config.type] && (
          <div className={`mt-4 p-4 border rounded-lg ${isStaticTechnology(config.type)
            ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
            : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
            }`}>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              <span className="font-semibold text-gray-900 dark:text-white">{projectTypes.find(p => p.id === config.type)?.name}:</span>{' '}
              {projectTypeDescriptions[config.type]}
            </p>
            {isStaticTechnology(config.type) && (
              <p className="text-xs text-yellow-800 dark:text-yellow-200 mt-2 font-medium">
                {t('deployApp.staticFrontendApps.staticAppNotice')}
              </p>
            )}
          </div>
        )}

        {/* Subdomain y App Slug: sólo para apps de backend/server */}
        {config.type && !isStaticTechnology(config.type) && (
          <div className="mt-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('deployApp.step1.subdomain')} *
              </label>
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  value={config.subdomain ?? ''}
                  onChange={(e) => handleSubdomainChange(e.target.value)}
                  placeholder={t('deployApp.step1.subdomainPlaceholder') || 'mi-app'}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                />
                <button
                  type="button"
                  onClick={handleVerifySubdomain}
                  disabled={!(config.subdomain ?? '').trim() || subdomainCheck === 'checking'}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {subdomainCheck === 'checking' ? (
                    <>
                      <LoadingOutlined spin className="mr-1" />
                      {t('deployApp.step1.verifying')}
                    </>
                  ) : (
                    t('deployApp.step1.verifySubdomain')
                  )}
                </button>
                {subdomainCheck === 'available' && (
                  <span className="flex items-center gap-1 text-green-600 dark:text-green-400" title={t('deployApp.step1.subdomainAvailable')}>
                    <CheckCircleOutlined />
                  </span>
                )}
                {subdomainCheck === 'unavailable' && (
                  <span className="flex items-center gap-1 text-red-600 dark:text-red-400" title={t('deployApp.step1.subdomainUnavailable')}>
                    <CloseCircleOutlined />
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('deployApp.step1.subdomainHint')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('deployApp.step1.appSlugLabel')} *
              </label>
              <input
                type="text"
                value={config.slug ?? ''}
                onChange={(e) => setConfig({ ...config, slug: e.target.value.replace(/\s/g, '').toLowerCase() })}
                placeholder={t('deployApp.step1.appSlugPlaceholder') || 'mi-app'}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('deployApp.step1.appSlugUrlPreview')}{' '}
                <span className="font-mono text-blue-600 dark:text-blue-400 break-all">
                  https://{(config.slug || 'slug').trim() || 'slug'}.{(config.subdomain || 'subdomain').trim() || 'subdomain'}.apps1.kubiy.site
                </span>
              </p>
            </div>
          </div>
        )}


        {/* Selección de Red y Cómputo - Solo para apps de tipo servidor */}
        {config.type && config.deploymentType && !hideInfrastructureSection && ((isSSRTechnology(config.type, config.framework) || backendProjectTypes.some(bt => bt.id === config.type)) && !isSSGTechnology(config.type, config.framework)) && (
          <div className="mt-6">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
              Configuración de Infraestructura
            </h4>

            <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-start gap-2 mb-3">
                <InfoCircleOutlined className="text-blue-600 dark:text-blue-400 text-lg mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">
                    💰 Las opciones compartidas son más económicas
                  </p>
                  <p className="text-xs text-blue-800 dark:text-blue-200 mb-3">
                    Las opciones compartidas ofrecen un mejor precio, mientras que las dedicadas proporcionan mayor aislamiento y rendimiento.
                  </p>
                </div>
              </div>

              <div className="mb-3 p-3 bg-white dark:bg-gray-800 rounded border border-blue-100 dark:border-blue-900">
                <h5 className="text-xs font-semibold text-gray-900 dark:text-white mb-2">
                  Red (Network)
                </h5>
                <div className="space-y-3 text-xs text-gray-700 dark:text-gray-300">
                  <div>
                    <span className="font-medium text-green-600 dark:text-green-400">Compartida:</span>
                    <span className="ml-2">Más económica y adecuada para la mayoría de aplicaciones.</span>
                    <ul className="mt-2 ml-4 list-disc space-y-1 text-gray-600 dark:text-gray-400">
                      <li>VPC dedicada por tenant</li>
                      <li>ALB compartido</li>
                      <li>NAT compartido</li>
                      <li className="italic">Aislamiento de red, no IP dedicada</li>
                    </ul>
                  </div>
                  <div>
                    <span className="font-medium text-purple-600 dark:text-purple-400">Dedicada:</span>
                    <span className="ml-2">Tienes una red completamente aislada y dedicada. Mayor seguridad y control, ideal para aplicaciones empresariales críticas.</span>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-white dark:bg-gray-800 rounded border border-blue-100 dark:border-blue-900">
                <h5 className="text-xs font-semibold text-gray-900 dark:text-white mb-2">
                  Cómputo (Compute)
                </h5>
                <div className="space-y-2 text-xs text-gray-700 dark:text-gray-300">
                  <div>
                    <span className="font-medium text-green-600 dark:text-green-400">Compartido:</span>
                    <span className="ml-2">Los recursos de procesamiento se comparten con otros usuarios. Más económico y suficiente para la mayoría de cargas de trabajo.</span>
                  </div>
                  <div>
                    <span className="font-medium text-purple-600 dark:text-purple-400">Dedicado:</span>
                    <span className="ml-2">Recursos de procesamiento exclusivos para tu aplicación. Mayor rendimiento y consistencia, ideal para aplicaciones con alta demanda.</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Tipo de Red *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setNetworkMode('shared');
                    if (networkMode === 'tenant') {
                      setComputeMode(null);
                    }
                  }}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    networkMode === 'shared'
                      ? 'border-green-600 dark:border-green-500 bg-green-50 dark:bg-green-900/20 ring-2 ring-green-200 dark:ring-green-800'
                      : 'border-gray-200 dark:border-gray-700 hover:border-green-500 dark:hover:border-green-500 bg-white dark:bg-gray-800'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-semibold text-gray-900 dark:text-white text-sm">
                      Red Compartida
                    </h5>
                    <span className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 rounded font-medium">
                      Más Económica
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Comparte la infraestructura de red con otros usuarios. Ideal para la mayoría de aplicaciones.
                  </p>
                </button>

                <button
                  type="button"
                  disabled
                  className="p-4 border-2 rounded-lg text-left transition-all border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 opacity-50 cursor-not-allowed"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-semibold text-gray-900 dark:text-white text-sm">
                      Red Dedicada
                    </h5>
                    <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded font-medium">
                      Próximamente
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Red completamente aislada y dedicada. Requiere cómputo dedicado.
                  </p>
                </button>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Tipo de Cómputo *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  type="button"
                  disabled
                  className="p-4 border-2 rounded-lg text-left transition-all border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 opacity-50 cursor-not-allowed"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-semibold text-gray-900 dark:text-white text-sm">
                      Cómputo Compartido
                    </h5>
                    <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded font-medium">
                      Próximamente
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Recursos compartidos. Disponible solo con red compartida.
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => setComputeMode('dedicated')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    computeMode === 'dedicated'
                      ? 'border-purple-600 dark:border-purple-500 bg-purple-50 dark:bg-purple-900/20 ring-2 ring-purple-200 dark:ring-purple-800'
                      : 'border-gray-200 dark:border-gray-700 hover:border-purple-500 dark:hover:border-purple-500 bg-white dark:bg-gray-800'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-semibold text-gray-900 dark:text-white text-sm">
                      Cómputo Dedicado
                    </h5>
                    <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300 rounded font-medium">
                      Mayor Rendimiento
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Recursos exclusivos para tu aplicación. Disponible con red compartida o dedicada.
                  </p>
                </button>
              </div>
            </div>

            <div className="mt-6">
              <KubiyComputeSizesTable />
            </div>
          </div>
        )}

        {/* Mensaje cuando falta seleccionar networkMode y computeMode para apps server */}
        {config.type && config.deploymentType && !hideInfrastructureSection && ((isSSRTechnology(config.type, config.framework) || backendProjectTypes.some(bt => bt.id === config.type)) && !isSSGTechnology(config.type, config.framework)) && (!networkMode || !computeMode) && (
          <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex items-start gap-2">
              <WarningOutlined className="text-yellow-600 dark:text-yellow-400 text-lg mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-yellow-900 dark:text-yellow-300 mb-1">
                  Selecciona la configuración de infraestructura
                </p>
                <p className="text-xs text-yellow-800 dark:text-yellow-200">
                  Por favor, selecciona el tipo de red y cómputo antes de ver los planes disponibles.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Mostrar planes disponibles */}
        {config.type && config.deploymentType && availablePlans && !hideAvailablePlansSection && (
          <div className="mt-6">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
              {t('deployApp.availablePlans.title')}
            </h4>
            <div className={`grid gap-4 ${availablePlans?.type === 'static' ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
              }`}>
              {availablePlans?.plans.map((plan) => (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => setSelectedPlan(plan.id)}
                  className={`border-2 rounded-lg px-4 pt-4 pb-4 text-left transition-all cursor-pointer flex flex-col items-start ${selectedPlan === plan.id
                    ? 'border-blue-600 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-200 dark:ring-blue-800'
                    : 'border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 bg-white dark:bg-gray-800'
                    }`}
                >
                  <div className="flex items-center justify-between w-full mb-3">
                    <div className="flex items-center gap-2">
                      <h5 className="font-semibold text-gray-900 dark:text-white text-sm">
                        {plan.name}
                      </h5>
                      {getPlanType(plan.id) && (
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                          getPlanType(plan.id) === 'shared'
                            ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300'
                            : 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300'
                        }`}>
                          {getPlanType(plan.id) === 'shared' ? 'Shared' : 'Dedicated'}
                        </span>
                      )}
                    </div>
                    {plan.price !== null ? (
                      <span className={`text-lg font-bold ${selectedPlan === plan.id
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-blue-600 dark:text-blue-400'
                        }`}>
                        ${plan.price}
                        <span className="text-xs font-normal text-gray-500 dark:text-gray-400">/mes</span>
                      </span>
                    ) : (
                      <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                        {t('deployApp.plans.custom')}
                      </span>
                    )}
                  </div>
                  {availablePlans?.type === 'static' && (plan as any).badge && (
                    <div className="w-full mb-3">
                      <div className={`inline-flex items-center gap-2 px-2 py-1 rounded text-xs font-medium mb-2 ${plan.id === 'static-dev' ? 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-300' :
                        plan.id === 'static-preview' ? 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300' :
                          plan.id === 'static-prod-lite' ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300' :
                            'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300'
                        }`}>
                        <span>{(plan as any).badge}</span>
                      </div>
                      {(plan as any).title && (
                        <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                          {(plan as any).title}
                        </p>
                      )}
                      {(plan as any).subtitle && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                          {(plan as any).subtitle}
                        </p>
                      )}
                      {(plan as any).description && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                          {(plan as any).description}
                        </p>
                      )}
                      {(plan as any).badgeSubtitle && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 italic mb-2">
                          {(plan as any).badgeSubtitle}
                        </p>
                      )}
                      {(plan as any).traffic && (
                        <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-700/50 rounded border border-gray-200 dark:border-gray-600">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                              {t('deployApp.availablePlans.trafficIncluded')}
                            </span>
                            <span className="text-xs font-semibold text-gray-900 dark:text-white">
                              {(plan as any).traffic}
                            </span>
                          </div>
                          {(plan as any).trafficMessage && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 italic">
                              {(plan as any).trafficMessage}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="w-full">
                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Incluye:
                    </p>
                    <ul className="space-y-2 w-full">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="text-xs text-gray-600 dark:text-gray-400 flex items-start gap-2">
                          <span className="text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0">•</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {selectedPlan === plan.id && (
                    <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800 w-full">
                      <span className="text-xs font-medium text-blue-600 dark:text-blue-400 flex items-center gap-1">
                        <CheckCircleOutlined /> {t('deployApp.availablePlans.selected')}
                      </span>
                    </div>
                  )}
                </button>
              ))}
            </div>
            {availablePlans?.type === 'static' && (
              <p className="mt-4 text-xs text-gray-500 dark:text-gray-400 italic">
                {t('deployApp.availablePlans.staticNote')}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Step1DeployApp;
